import { Dispatch, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useUnmount } from 'react-use';

import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import {
  getAccountKnoOrganizationsWithDistrictSelectOptions,
  getAccountProsecutorOrganizationsWithDistrictSelectOptions,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { NotMatchingFnsFieldTypesEnum } from 'KnmPackage/MainModule/enums/NotMatchingFnsFieldTypesEnum';
import { KnmErpApproveInfo } from 'KnmErpPackage/KnmErpApproveInfoModule/components/KnmErpApproveInfo';
import { KnmErpCancellationInfo } from 'KnmErpPackage/KnmErpCancellationInfoModule/components/KnmErpCancellationInfo';
import { KnmErpChecklists } from 'KnmErpPackage/KnmErpChecklistsModule/components/KnmErpChecklists';
import { KnmDocuments } from 'KnmPackage/KnmDocumentsModule/components/KnmDocuments';
import { KnmHistories } from 'KnmPackage/KnmHistoriesModule/components/KnmHistories';
import { KnmInfo } from 'KnmPackage/KnmInfoModule/components/KnmInfo';
import { KnmErpKnoOrganization } from 'KnmErpPackage/KnmErpKnoOrganizationModule/components/KnmErpKnoOrganization';
import { KnmErpLawsuits } from 'KnmErpPackage/KnmErpLawsuitsModule/components/KnmErpLawsuits';
import { KnmErpLinks } from 'KnmErpPackage/KnmErpLinksModule/components/KnmErpLinks';
import { KnmNoticeInfo } from 'KnmErpPackage/KnmNoticeInfoModule/components/KnmNoticeInfo';
import { KnmErpPrimaryInfo } from 'KnmErpPackage/KnmErpPrimaryInfoModule/components/KnmErpPrimaryInfo';
import { KnmErpRequirements } from 'KnmErpPackage/KnmErpRequirementsModule/components/KnmErpRequirements';
import { KnmErpResults } from 'KnmErpPackage/KnmErpResultsModule/components/KnmErpResults';
import { KnmSignInfo } from 'KnmPackage/KnmSignInfoModuleOld/components/KnmSignInfo';
import { KnmErpSubject } from 'KnmErpPackage/KnmErpSubjectModule/components/KnmErpSubject';
import { KnmErpViolations } from 'KnmErpPackage/KnmErpViolationsModule/components/KnmErpViolations';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectSelectOption';
import { IKnmErpObjectResultSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectResultSelectOption';
import { IKnmErpResultViolationSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpResultViolationSelectOption';
import { ResultsOpenedFlagsType } from 'KnmPackage/MainModule/types/ResultsOpenedFlagsType';
import {
  getKnmErpKnoSupervisionUuid,
  getKnmIsNew,
  getKnmStatusCode,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmPrevious } from 'KnmPackage/KnmPreviousModule/components/KnmPrevious';
import { KnmSignatureInfoStatusesEnum } from 'APIPackage/KnmModule/enums/KnmSignatureInfoStatusesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  dictionariesFederalLawsGet,
  dictionariesKnmSubjectTypesGet,
  dictionariesKnmKindsItemGet,
  dictionariesKnoOrganizationsItemGet,
  dictionariesKnoSupervisionGet,
  dictionariesSubjectTypesItemGet,
  dictionariesRiskCategoriesItemGet,
  dictionariesErvkCheckListGet,
  dictionariesSupervisionTypesItemGet,
  dictionariesTargetEntityItemGet,
  dictionariesDangerClassesItemGet,
  dictionariesRiskIndicatorsItemGet,
  dictionariesParticipantPositionsItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { catalogErpKnmConditionsGet, catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';

import { IKnmErpForm } from '../interfaces/IKnmErpForm';
import { getKnmErpInitialFormValues } from '../selectors/knmErpSelectors';
import { knmErpResetInitialFormValues } from '../actions/knmErpActions';
import { knmInfoGetPlannedActivities } from '../../../KnmPackage/MainModule/actions/knmActions';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

const OBJECTS_NOT_FOUND_ERROR = '"Объекты проведения КНМ" не найдены';
const RESULTS_NOT_FOUND_ERROR = '"Результаты проведения КНМ" не найдены';
const VIOLATIONS_NOT_FOUND_ERROR = '"Выявленные нарушения" не найдены';
const KNO_ORGANIZATION_NOT_FOUND_ERROR = '"Наименование органа контроля" не найдено';
const SUPERVISION_NOT_FOUND_ERROR = '"Вид государственного контроля (надзора)" не найдено';

export interface IKnmErpContent {
  formRef: IKnmFormRef;
  knmData: IKnm | undefined;
  values: IKnmErpForm;
  isWatchOnly: boolean;
  resultsOpenedFlags: ResultsOpenedFlagsType;
  registryOrganizationNameFns: string | undefined;
  registryLegalAddressFns: string | undefined;
  notMatchingFnsFields: NotMatchingFnsFieldTypesEnum[];
  onLegalBasesOpenButtonClick: () => void;
  makeViolationsLegalBasesOpenButtonClickHandler: (violationIndex: number) => () => void;
  changeResultsOpenedFlags: (id: string, value: boolean) => void;
}

interface IKnmErpContentStoreProps {
  statusCode: IKnmStatus['code'] | undefined;
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  isNewKnm: boolean;
  initialFormValues?: IKnmErpForm;
  settings: IAdminSetting[];
}

const KnmErpContentWithoutStore = ({
  formRef,
  knmData,
  values,
  isWatchOnly,
  resultsOpenedFlags,
  registryOrganizationNameFns,
  registryLegalAddressFns,
  notMatchingFnsFields,
  onLegalBasesOpenButtonClick,
  makeViolationsLegalBasesOpenButtonClickHandler,
  changeResultsOpenedFlags,
  isNewKnm,
  statusCode,
  userKnoOrganisationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  initialFormValues,
  settings,
}: IKnmErpContent & IKnmErpContentStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const federalLawCode = values.federalLaw?.code ?? values.federalLaw?.number;

  useEffect(() => {
    formRef.current.reset(initialFormValues);
  }, [formRef, initialFormValues]);

  const generatedKnmErpKnoSupervisionUuid = useSelector(getKnmErpKnoSupervisionUuid);
  const reasonId = useMemo(() => {
    const { reasonsErvk = [], reasons = [] } = values;

    return [...reasonsErvk, ...reasons]?.map(x => x?.type?.ervkUuid)?.find(uuid => uuid) || undefined;
  }, [values.reasons, values.reasonsErvk]);
  const objectsTypeIds = useMemo(() => {
    const { objectsErvk } = values;
    const result: string[] = [];

    objectsErvk?.forEach(object => {
      if (object && object.objectType && object.objectType.value) {
        result.push(object.objectType.value.dictVersionId);
      }
    });

    return result;
  }, [values.reasonsErvk]);

  /*
    ----------Получение словарей для селекторов----------
  */
  useEffect(() => {
    dispatch(dictionariesKnoOrganizationsItemGet());
    dispatch(dictionariesFederalLawsGet());
    dispatch(dictionariesKnmSubjectTypesGet());
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_294));
  }, [dispatch]);

  useEffect(() => {
    const { knoOrganizationErvk, supervisionTypeErvk } = values;
    const knoOrganizationId = knoOrganizationErvk?.value.dictVersionId;
    const supervisionTypeId = supervisionTypeErvk?.value.dictVersionId;

    if (knoOrganizationId && supervisionTypeId) {
      dispatch(dictionariesKnoSupervisionGet(knoOrganizationId, supervisionTypeId));
      dispatch(knmInfoGetPlannedActivities(knoOrganizationId, supervisionTypeId));
    }
  }, [values.knoOrganizationErvk, values.supervisionTypeErvk]);

  useEffect(() => {
    const { knoOrganizationErvk, federalLaw, type, typeErvk } = values;
    const knoOrganizationId = knoOrganizationErvk?.value.dictVersionId;
    const knmTypeCode = type?.code ?? typeErvk?.code;
    const federalLawDictVersionId = federalLaw?.value?.dictVersionId;

    if (knoOrganizationId && federalLawDictVersionId) {
      dispatch(
        dictionariesSupervisionTypesItemGet(
          [knoOrganizationId],
          knmTypeCode === 'PP',
          federalLawDictVersionId ? [federalLawDictVersionId] : undefined,
        ),
      );
    }
  }, [values.knoOrganizationErvk, values.federalLaw, values.type, values.typeErvk]);

  useEffect(() => {
    const { knoOrganizationErvk, supervisionTypeErvk, federalLaw, type, typeErvk, kindErvk } = values;
    const knoOrganizationId = knoOrganizationErvk?.value.dictVersionId;
    const supervisionTypeId = supervisionTypeErvk?.value.dictVersionId;
    const knmTypeCode = type?.code ?? typeErvk?.code;
    const federalLawDictVersionId = federalLaw?.value?.dictVersionId;
    const kindKnmId = kindErvk?.value.dictVersionId;

    if (knoOrganizationId && supervisionTypeId && federalLawDictVersionId && knmTypeCode) {
      dispatch(
        catalogErpKnmConditionsGet(
          knoOrganizationId,
          supervisionTypeId,
          knmTypeCode,
          federalLawDictVersionId,
          kindKnmId,
        ),
      );
    }
  }, [
    values.knoOrganizationErvk,
    values.supervisionTypeErvk,
    values.federalLaw,
    values.type,
    values.typeErvk,
    values.kindErvk,
  ]);

  useEffect(() => {
    if (generatedKnmErpKnoSupervisionUuid) {
      dispatch(dictionariesSubjectTypesItemGet(generatedKnmErpKnoSupervisionUuid));
      dispatch(dictionariesParticipantPositionsItemGet(generatedKnmErpKnoSupervisionUuid));
      dispatch(dictionariesRiskCategoriesItemGet(generatedKnmErpKnoSupervisionUuid));
      dispatch(dictionariesRiskIndicatorsItemGet(generatedKnmErpKnoSupervisionUuid));
    }
  }, [generatedKnmErpKnoSupervisionUuid]);

  useEffect(() => {
    const { knoOrganizationErvk, supervisionTypeErvk } = values;
    const knoOrganizationId = knoOrganizationErvk?.value.dictVersionId;
    const supervisionTypeId = supervisionTypeErvk?.value.dictVersionId;

    if (knoOrganizationId && supervisionTypeId) {
      if (reasonId) {
        dispatch(
          dictionariesKnmKindsItemGet({
            knoVersionId: knoOrganizationId,
            supervisionVersionId: supervisionTypeId,
            conditionUuid: reasonId,
          }),
        );
        dispatch(
          dictionariesTargetEntityItemGet({
            knoVersionId: knoOrganizationId,
            supervisionVersionId: supervisionTypeId,
            conditionUuid: reasonId,
          }),
        );
      }
    }
  }, [values.knoOrganizationErvk, values.supervisionTypeErvk, reasonId]);

  useEffect(() => {
    const { type, typeErvk } = values;
    const knmTypeCode = type?.code ?? typeErvk?.code;

    if (generatedKnmErpKnoSupervisionUuid && reasonId) {
      dispatch(
        dictionariesDangerClassesItemGet(generatedKnmErpKnoSupervisionUuid, reasonId, undefined, knmTypeCode === 'PP'),
      );
      dispatch(
        dictionariesRiskCategoriesItemGet(
          generatedKnmErpKnoSupervisionUuid,
          reasonId,
          undefined,
          undefined,
          knmTypeCode === 'PP',
        ),
      );
    }
  }, [generatedKnmErpKnoSupervisionUuid, reasonId, values.type, values.typeErvk]);

  useEffect(() => {
    const { supervisionTypeErvk, kindErvk } = values;
    const supervisionTypeId = supervisionTypeErvk?.value.dictVersionId;
    const kindKnmId = kindErvk?.value.dictVersionId;

    if (kindKnmId && supervisionTypeId) {
      if (objectsTypeIds?.length > 0) {
        dispatch(
          dictionariesErvkCheckListGet({
            controlKindId: supervisionTypeId,
            knmKindId: kindKnmId,
            subjectKindIds: objectsTypeIds,
          }),
        );
      } else {
        dispatch(
          dictionariesErvkCheckListGet({
            controlKindId: supervisionTypeId,
            knmKindId: kindKnmId,
          }),
        );
      }
    }
  }, [values.supervisionTypeErvk, values.kindErvk, objectsTypeIds]);

  /*
    --------------------
  */

  useUnmount(() => {
    dispatch(knmErpResetInitialFormValues());
  });

  const formType = values.type;
  const isUseERVKParamTables = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );

  const formTypeCode = isUseERVKParamTables ? values.typeErvk?.code : values.type?.code;
  const formObjects = values.objects;
  const formObjectsRef = useRef(formObjects);

  formObjectsRef.current = formObjects;
  const formObjectsResults = values.objectsResults;
  const formKnmReasons = values.reasons;
  const formObjectsResultsRef = useRef(formObjectsResults);

  formObjectsResultsRef.current = formObjectsResults;
  const isObjectsFound = formObjects?.some(item => item?.address);
  const objectsError = !isObjectsFound && !values.objectsErvk?.length ? OBJECTS_NOT_FOUND_ERROR : '';
  const isResultsFound = formObjectsResults && formObjectsResults.length;
  const resultsError = !isResultsFound ? RESULTS_NOT_FOUND_ERROR : '';
  const isViolationsFound = values.violations && values.violations.length;
  const violationsError = !isViolationsFound ? VIOLATIONS_NOT_FOUND_ERROR : '';
  const knoOrganizationError =
    !values.knoOrganization && !values.knoOrganizationErvk ? KNO_ORGANIZATION_NOT_FOUND_ERROR : '';
  const checklistsError = knoOrganizationError || objectsError;
  const supervisionError = !values.supervisionType && !values.supervisionTypeErvk ? SUPERVISION_NOT_FOUND_ERROR : '';
  const formOrganizationName = values.organizationName;
  const formRequirements = values.requirements;

  const isChecklistsHidden = formTypeCode === KnmTypeCodesEnum.VP || formTypeCode === KnmTypeCodesEnum.KZ;
  const isLinksHidden = !statusCode;

  const isSomeImpossibleConductionReasonNotEmpty =
    Boolean(values.impossibleConductionReason?.trim()) ||
    formObjectsResults?.some(formObjectResult => {
      const additionalInfoImpossibleReasonItem = formObjectResult.additionalInfo?.find(
        additionalInfoItem => additionalInfoItem?.type?.code === KnmResultInfoTypeCodesEnum.UNIMPOSSIBLE_REASON_I,
      );

      return !!additionalInfoImpossibleReasonItem?.information;
    }) ||
    false;

  const knmObjectsOptions = useMemo(
    () =>
      values.objects?.reduce((filteredKnmObjectsOptions: IKnmErpObjectSelectOption[], object) => {
        const objectAddress = object?.notInFIAS ? object?.manualAddress : object?.address;

        if (objectAddress) {
          filteredKnmObjectsOptions.push({
            id: object.listId,
            address: objectAddress,
            guid: object.listId,
            label: objectAddress,
            value: objectAddress,
          });
        }

        return filteredKnmObjectsOptions;
      }, []),
    [values.objects],
  );

  const knmObjectsErvkOptions = useMemo(() => {
    return values.objectsErvk?.reduce((filteredKnmObjectsOptions: IKnmErpObjectSelectOption[], object) => {
      const objectAddress = object.notInFIAS ? object.manualAddress : object.address;

      if (objectAddress) {
        filteredKnmObjectsOptions.push({
          id: object.listId,
          address: objectAddress,
          guid: object.guid,
          label: objectAddress,
          value: objectAddress,
        });
      }

      return filteredKnmObjectsOptions;
    }, []);
  }, [values.objectsErvk]);

  const knmChecklistsObjectsOptions = () => {
    if (checklistsError) return undefined;

    return isUseERVKParamTables ? knmObjectsErvkOptions : knmObjectsOptions;
  };

  const knmObjectsResultsSelectOptions = useMemo(
    () =>
      values.objectsResults?.reduce(
        (filteredKnmObjectsResultsOptions: IKnmErpObjectResultSelectOption[], objectResult) => {
          if (objectResult.object) {
            filteredKnmObjectsResultsOptions.push({
              id: objectResult.object.id,
              label: objectResult.object.address,
              value: objectResult.object.address,
            });
          }

          return filteredKnmObjectsResultsOptions;
        },
        [],
      ),
    [values.objectsResults],
  );

  const knmResultsViolationsSelectOptions = useMemo(
    () =>
      values.violations?.reduce(
        (filteredKnmResultsViolationsOptions: IKnmErpResultViolationSelectOption[], resultViolation, currentIndex) => {
          if (resultViolation.result) {
            filteredKnmResultsViolationsOptions.push({
              id: resultViolation.listId,
              label: `Нарушение №${currentIndex + 1} ${resultViolation.result.label}`,
              value: resultViolation.result.value,
            });
          }

          return filteredKnmResultsViolationsOptions;
        },
        [],
      ),
    [values.violations],
  );

  useEffect(() => {
    if (userProsecutorOrganisationsSelectOptions && !knmData?.id) {
      const byDefault = userProsecutorOrganisationsSelectOptions.find(i => i.byDefault);

      if (byDefault) {
        formRef?.current.change('prosecutorOrganization', byDefault);
      }
    }
  }, [knmData, values, userProsecutorOrganisationsSelectOptions]);

  return (
    <>
      <KnmErpPrimaryInfo
        creationDate={knmData?.creationDate}
        erpId={knmData?.erpId}
        knmStatusCode={statusCode}
        knmCreatedDate={knmData?.created}
        planId={knmData?.planId}
        subjectTypeCode={values.subjectType?.code}
        supervisionType={values.supervisionType}
        typeCode={formTypeCode}
        watchOnly={isWatchOnly}
        settings={settings}
      />
      <KnmInfo
        extensionOfKnmTimeChecked={values.extensionOfKnmTime}
        typeCode={formTypeCode}
        watchOnly={isWatchOnly}
        federalLawCode={federalLawCode}
        onLegalBasesOpenButtonClick={onLegalBasesOpenButtonClick}
      />
      <KnmErpRequirements
        formRequirements={formRequirements}
        error={knoOrganizationError || supervisionError}
        watchOnly={isWatchOnly}
      />
      <KnmErpChecklists
        checklistsHidden={isChecklistsHidden}
        error={checklistsError}
        formKindValue={values.kind}
        formTypeValue={formType}
        knmObjectsOptions={knmChecklistsObjectsOptions()}
        watchOnly={isWatchOnly}
        knmData={knmData}
        settings={settings}
      />
      <KnmErpApproveInfo
        approveRequired={values.reasonsErvk?.some(reason => reason?.type?.agreement === 'Требуется')}
        inspectionDecisionId={values.inspectionDecision?.id}
        formKnmReasons={formKnmReasons}
        planId={knmData?.planId}
        typeCode={formTypeCode}
        watchOnly={isWatchOnly}
        startDate={values.startDate}
        settings={settings}
        userProsecutorOrganisationsSelectOptions={userProsecutorOrganisationsSelectOptions}
        knmData={knmData}
      />
      <KnmErpKnoOrganization
        knoOrganisationsSelectOptions={userKnoOrganisationsSelectOptions}
        knmCreatedDate={knmData?.created}
        statusCode={statusCode}
        watchOnly={isWatchOnly}
        settings={settings}
      />
      <KnmErpSubject
        inn={values.inn}
        knmCreatedDate={knmData?.created}
        newKnm={isNewKnm}
        notMatchingFnsFields={notMatchingFnsFields}
        objects={formObjects}
        ogrn={values.ogrn}
        organizationAutocomplete={values.organizationAutocomplete}
        organizationName={formOrganizationName}
        registryLegalAddressFns={registryLegalAddressFns}
        registryOrganizationNameFns={registryOrganizationNameFns}
        watchOnly={isWatchOnly}
        settings={settings}
      />
      <KnmNoticeInfo watchOnly={isWatchOnly} />
      <KnmErpResults
        error={objectsError}
        knmObjectsOptions={isUseERVKParamTables ? knmObjectsErvkOptions : knmObjectsOptions}
        objectsResults={formObjectsResults}
        resultsOpenedFlags={resultsOpenedFlags}
        startDate={values.startDate}
        watchOnly={isWatchOnly}
        changeResultsOpenedFlags={changeResultsOpenedFlags}
      />
      <KnmErpViolations
        disabled={isSomeImpossibleConductionReasonNotEmpty}
        error={objectsError || resultsError}
        knmObjectsResultsSelectOptions={knmObjectsResultsSelectOptions}
        violationsNumber={values.violations?.length || 0}
        watchOnly={isWatchOnly}
        makeLegalBasesOpenButtonClickHandler={makeViolationsLegalBasesOpenButtonClickHandler}
      />
      <KnmErpLawsuits
        error={objectsError || resultsError || violationsError}
        knmResultsViolationsSelectOptions={knmResultsViolationsSelectOptions}
        violationsLawsuits={values.violationsLawsuits}
        watchOnly={isWatchOnly}
      />
      <KnmErpCancellationInfo
        error={objectsError || resultsError}
        knmObjectsResultsSelectOptions={knmObjectsResultsSelectOptions}
        resultsCancellationInfo={values.resultsCancellationInfo}
        watchOnly={isWatchOnly}
      />
      <KnmSignInfo
        title="Сведения о подписании КНМ"
        notificationText="Паспорт КНМ ранее не был подписан"
        signaturesInfo={knmData?.signatureInfo?.filter(item => item.status !== KnmSignatureInfoStatusesEnum.INVALID)}
      />
      <KnmDocuments
        watchOnly={isWatchOnly}
        formInstance={formRef.current}
        title="Документы"
        knmDocuments={knmData?.documents}
      />
      <KnmPrevious dataInn={knmData?.organization?.inn} isNewKnm={isNewKnm} dataOgrn={knmData?.organization?.ogrn} />
      {!isLinksHidden && <KnmErpLinks formLinkType={values.linkType} erpId={knmData?.erpId} watchOnly={isWatchOnly} />}
      <KnmHistories title="История изменений" executionTitle="История проведения КНМ" />
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpContentStoreProps => ({
  statusCode: getKnmStatusCode(state),
  userKnoOrganisationsSelectOptions: getAccountKnoOrganizationsWithDistrictSelectOptions(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  isNewKnm: getKnmIsNew(state) || false,
  initialFormValues: getKnmErpInitialFormValues(state),
  settings: getAdminFetchSettings(state),
});

export const KnmErpContent = connect(mapStateToProps)(KnmErpContentWithoutStore);
